/**
 * @fileoverview Flagged Transactions — shared callout blocks
 * @module jobs/phases/review/flaggedTransactions/Callout
 *
 * Proper visual callouts for the statutory context that was previously
 * rendered as inline grey text: review window, insolvency basis, defence
 * indicators, recovery notes. One component, five tones.
 *
 * `critical` is for a gap in the material that stops the head being answered —
 * loud on purpose, because a head that reports nothing because the records are
 * absent must never read like a head that found nothing.
 */

import type { LucideIcon } from "lucide-react";
import {
  CalendarRange,
  Scale,
  ShieldAlert,
  HandCoins,
  FileWarning,
} from "lucide-react";
import { cn } from "@/utils/misc";

type Tone = "info" | "legal" | "warning" | "recovery" | "critical";

const TONES: Record<
  Tone,
  { container: string; icon: string; title: string; Icon: LucideIcon }
> = {
  info: {
    container:
      "border-sky-200/80 bg-sky-50/60 dark:border-sky-900/50 dark:bg-sky-950/20",
    icon: "text-sky-600 dark:text-sky-400",
    title: "text-sky-800 dark:text-sky-300",
    Icon: CalendarRange,
  },
  legal: {
    container:
      "border-slate-200 bg-slate-50/80 dark:border-slate-800 dark:bg-slate-900/40",
    icon: "text-slate-500 dark:text-slate-400",
    title: "text-slate-700 dark:text-slate-300",
    Icon: Scale,
  },
  warning: {
    container:
      "border-amber-200/80 bg-amber-50/60 dark:border-amber-900/50 dark:bg-amber-950/20",
    icon: "text-amber-600 dark:text-amber-400",
    title: "text-amber-800 dark:text-amber-300",
    Icon: ShieldAlert,
  },
  recovery: {
    container:
      "border-emerald-200/80 bg-emerald-50/50 dark:border-emerald-900/50 dark:bg-emerald-950/20",
    icon: "text-emerald-600 dark:text-emerald-400",
    title: "text-emerald-800 dark:text-emerald-300",
    Icon: HandCoins,
  },
  critical: {
    container:
      "border-rose-300 bg-rose-50/80 dark:border-rose-900/60 dark:bg-rose-950/30",
    icon: "text-rose-600 dark:text-rose-400",
    title: "text-rose-800 dark:text-rose-300",
    Icon: FileWarning,
  },
};

export function Callout(props: {
  tone: Tone;
  title: string;
  icon?: LucideIcon;
  children: React.ReactNode;
  className?: string;
}) {
  const tone = TONES[props.tone];
  const Icon = props.icon ?? tone.Icon;
  return (
    <div
      className={cn(
        "flex gap-2.5 rounded-lg border px-3 py-2.5",
        tone.container,
        props.className,
      )}
    >
      <Icon className={cn("mt-0.5 h-4 w-4 shrink-0", tone.icon)} />
      <div className="min-w-0 flex-1">
        <p
          className={cn(
            "mb-0.5 text-[10px] font-semibold uppercase tracking-widest",
            tone.title,
          )}
        >
          {props.title}
        </p>
        <div className="text-xs leading-relaxed text-foreground/90">
          {props.children}
        </div>
      </div>
    </div>
  );
}
